// Central cookie-consent state for TudoConversor.
//
// Storage model (localStorage):
//   tc_consent_status    -> 'accepted' | 'rejected' | 'custom' | null (no choice yet)
//   tc_consent_analytics -> 'granted' | 'denied'   (only meaningful when status === 'custom')
//   tc_consent_ads       -> 'granted' | 'denied'   (only meaningful when status === 'custom')
//
// Legacy key `tc_cookie_consent` ('accepted' | 'rejected') from the previous
// banner is migrated on read so existing visitors keep their recorded choice.
//
// Default state is DENIED for everything until the visitor makes an explicit
// choice. Continued navigation is NEVER treated as consent.

export const CONSENT_KEYS = {
  status: 'tc_consent_status',
  analytics: 'tc_consent_analytics',
  ads: 'tc_consent_ads',
  legacy: 'tc_cookie_consent',
};

export const CONSENT_EVENT = 'tc_consent_change';

/** 'accepted' | 'rejected' | 'custom' | null */
export function getConsentStatus() {
  if (typeof window === 'undefined') return null;
  let s = window.localStorage.getItem(CONSENT_KEYS.status);
  if (!s) {
    const old = window.localStorage.getItem(CONSENT_KEYS.legacy);
    if (old === 'accepted') s = 'accepted';
    else if (old === 'rejected') s = 'rejected';
  }
  return s || null;
}

export function hasChosen() {
  return getConsentStatus() !== null;
}

export function getAnalyticsConsent() {
  const s = getConsentStatus();
  if (s === 'accepted') return 'granted';
  if (s === 'custom') {
    return window.localStorage.getItem(CONSENT_KEYS.analytics) === 'granted' ? 'granted' : 'denied';
  }
  return 'denied'; // 'rejected' or null -> denied
}

export function getAdsConsent() {
  const s = getConsentStatus();
  if (s === 'accepted') return 'granted';
  if (s === 'custom') {
    return window.localStorage.getItem(CONSENT_KEYS.ads) === 'granted' ? 'granted' : 'denied';
  }
  return 'denied'; // 'rejected' or null -> denied
}

/**
 * Persist a consent choice and sync Google Consent Mode + notify loaders.
 * @param {Object} opts
 * @param {'accepted'|'rejected'|'custom'} opts.status
 * @param {'granted'|'denied'} [opts.analytics]  (custom only)
 * @param {'granted'|'denied'} [opts.ads]          (custom only)
 */
export function setConsent({ status, analytics = 'denied', ads = 'denied' }) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CONSENT_KEYS.status, status);
  if (status === 'custom') {
    window.localStorage.setItem(CONSENT_KEYS.analytics, analytics === 'granted' ? 'granted' : 'denied');
    window.localStorage.setItem(CONSENT_KEYS.ads, ads === 'granted' ? 'granted' : 'denied');
  }
  // Clear legacy key so the new model is the single source of truth.
  window.localStorage.removeItem(CONSENT_KEYS.legacy);

  const analyticsGranted = status === 'accepted' || (status === 'custom' && analytics === 'granted');
  const adsGranted = status === 'accepted' || (status === 'custom' && ads === 'granted');

  // Update Google Consent Mode v2. gtag() is defined inline in index.html and
  // pushes to dataLayer, so this works even before gtag.js is loaded.
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: adsGranted ? 'granted' : 'denied',
      ad_user_data: adsGranted ? 'granted' : 'denied',
      ad_personalization: adsGranted ? 'granted' : 'denied',
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
    });
  }

  // Notify consent-gated script loaders (AdSense, gtag.js) to (re)evaluate.
  window.dispatchEvent(new Event(CONSENT_EVENT));
}
